import type { VersionPlatform } from '@patch-manager/shared';

export interface DetectedPlatform {
  os: 'windows' | 'macos' | 'linux' | 'unknown';
  architecture: 'x64' | 'x86' | 'arm64' | 'universal' | 'unknown';
}

/**
 * Detects the user's operating system and architecture from browser APIs
 * @returns DetectedPlatform object with os and architecture
 */
export function detectPlatform(): DetectedPlatform {
  const platform = navigator.platform.toLowerCase();
  const userAgent = navigator.userAgent.toLowerCase();

  let os: DetectedPlatform['os'] = 'unknown';
  let architecture: DetectedPlatform['architecture'] = 'unknown';

  // Detect OS
  if (platform.includes('win') || userAgent.includes('windows')) {
    os = 'windows';
  } else if (
    platform.includes('mac') ||
    userAgent.includes('macintosh') ||
    userAgent.includes('mac os')
  ) {
    os = 'macos';
  } else if (platform.includes('linux') || userAgent.includes('linux')) {
    os = 'linux';
  }

  // Detect Architecture
  if (os === 'macos') {
    // Check for Apple Silicon (M1/M2/M3)
    if (
      userAgent.includes('arm') ||
      userAgent.includes('aarch64') ||
      // Navigator.userAgentData is a newer API that might provide better detection
      // @ts-ignore - userAgentData may not be available in all browsers
      (navigator.userAgentData?.platform === 'macOS' && !platform.includes('intel'))
    ) {
      architecture = 'arm64';
    } else if (platform.includes('intel') || platform.includes('x86')) {
      architecture = 'x64';
    } else {
      // Default to universal for macOS if we can't determine
      architecture = 'universal';
    }
  } else if (os === 'windows' || os === 'linux') {
    // Check for 64-bit vs 32-bit
    if (
      platform.includes('x86_64') ||
      platform.includes('x64') ||
      platform.includes('amd64') ||
      platform.includes('wow64') ||
      userAgent.includes('win64') ||
      userAgent.includes('x64')
    ) {
      architecture = 'x64';
    } else if (
      platform.includes('x86') ||
      platform.includes('i686') ||
      platform.includes('i386') ||
      platform.includes('win32')
    ) {
      architecture = 'x86';
    } else if (platform.includes('arm') || userAgent.includes('arm')) {
      architecture = 'arm64';
    } else {
      // Default to x64 for modern systems
      architecture = 'x64';
    }
  }

  return { os, architecture };
}

/**
 * Finds the best matching platform from available options based on detected platform
 * @param platforms Array of available platforms
 * @param detected DetectedPlatform object from detectPlatform()
 * @returns The best matching VersionPlatform or null if no match found
 */
export function findMatchingPlatform(
  platforms: VersionPlatform[],
  detected: DetectedPlatform
): VersionPlatform | null {
  if (platforms.length === 0) return null;
  if (platforms.length === 1) return platforms[0];

  // Priority 1: Exact match (OS + architecture)
  const exactMatch = platforms.find(
    (p) => p.os_platform === detected.os && p.architecture === detected.architecture
  );
  if (exactMatch) return exactMatch;

  // Priority 2: Same OS + universal architecture
  const universalMatch = platforms.find(
    (p) => p.os_platform === detected.os && p.architecture === 'universal'
  );
  if (universalMatch) return universalMatch;

  // Priority 3: Same OS + any architecture
  const sameOsMatch = platforms.find((p) => p.os_platform === detected.os);
  if (sameOsMatch) return sameOsMatch;

  // Priority 4: If we're on ARM and there's an x64 version for the same OS (Rosetta/emulation)
  if (detected.architecture === 'arm64') {
    const x64Match = platforms.find(
      (p) => p.os_platform === detected.os && p.architecture === 'x64'
    );
    if (x64Match) return x64Match;
  }

  // No match found
  return null;
}

/**
 * Gets a human-readable label for a platform
 * @param os Operating system
 * @param architecture Architecture
 * @returns Human-readable string like "Windows 64-bit" or "macOS (Apple Silicon)"
 */
export function getPlatformLabel(os: string, architecture: string): string {
  const osLabels: Record<string, string> = {
    windows: 'Windows',
    macos: 'macOS',
    linux: 'Linux',
    none: 'Platform Independent',
  };

  const archLabels: Record<string, string> = {
    x64: '64-bit',
    x86: '32-bit',
    arm64: '(ARM)',
    arm: '(ARM)',
    universal: '(Universal)',
    none: '',
  };

  const osLabel = osLabels[os] || os;
  const archLabel = archLabels[architecture] || architecture;

  // Special case for macOS ARM
  if (os === 'macos' && architecture === 'arm64') {
    return `${osLabel} (Apple Silicon)`;
  }

  // Special case for macOS Universal
  if (os === 'macos' && architecture === 'universal') {
    return `${osLabel} (Universal)`;
  }

  return archLabel ? `${osLabel} ${archLabel}` : osLabel;
}
